import React, { useMemo } from 'react';
import { HeartIcon } from '@heroicons/react/24/outline';
import CloseIcon from '@heroicons/react/24/outline/XMarkIcon';
import { ImageProps } from 'components/commercetools-ui/atoms/image';
import Cart from 'components/commercetools-ui/organisms/cart-slideout';
import { Link } from 'components/commercetools-ui/organisms/header/types';
import Wishlist from 'components/commercetools-ui/organisms/wishlist';
import CartIcon from 'components/icons/cart';
import useClassNames from 'helpers/hooks/useClassNames';
import { useFormat } from 'helpers/hooks/useFormat';
import { Cart as CartShape, Discount, LineItem as CartLineItem } from 'types/entity/cart';
import { LineItem as WishlistLineItem, Wishlist as WishlistShape } from 'types/entity/wishlist';

export type MenuState = 'wishlist' | 'cart';

export interface SlideOutProps {
  cart?: CartShape;
  isEmpty?: boolean;
  onApplyDiscountCode?: (code: string) => Promise<void>;
  onRemoveDiscountCode?: (discount: Discount) => Promise<void>;
  totalCartItems?: number;
  totalWishlistItems?: number;
  onRemoveItem(itemId: string): Promise<void>;
  onUpdateItem(itemId: string, quantity: number): Promise<void>;
  OnMoveToWishlist(lineItem: CartLineItem): Promise<void>;
  wishlist?: WishlistShape;
  onRemoveFromWishlist?: (lineItemId: string) => Promise<void>;
  onMoveToCart?: (lineItem: WishlistLineItem) => Promise<void>;
  onClearWishlist?: () => Promise<void>;
  state?: MenuState;
  changeState?: (newState?: MenuState) => void;
  onClose?: () => void;
  emptyCartTitle: string;
  emptyCartSubtitle: string;
  emptyCartImage: ImageProps;
  emptyCartCategories: Link[];
  emptyWishlistTitle: string;
  emptyWishlistSubtitle: string;
  emptyWishlistImage: ImageProps;
  emptyWishlistCategories: Link[];
  enableCartState?: boolean;
  enableWishlistState?: boolean;
}

const Slideout: React.FC<SlideOutProps> = ({
  cart,
  isEmpty,
  totalCartItems = 0,
  onApplyDiscountCode,
  onRemoveDiscountCode,
  totalWishlistItems = 0,
  onRemoveItem,
  onUpdateItem,
  OnMoveToWishlist,
  wishlist,
  onRemoveFromWishlist,
  onMoveToCart,
  onClearWishlist,
  state,
  onClose,
  emptyCartTitle,
  emptyCartSubtitle,
  emptyCartImage,
  emptyCartCategories,
  emptyWishlistTitle,
  emptyWishlistSubtitle,
  emptyWishlistImage,
  emptyWishlistCategories,
}) => {
  const { formatMessage: formatCartMessage } = useFormat({ name: 'cart' });
  const { formatMessage: formatWishlistMessage } = useFormat({ name: 'wishlist' });

  const title = useMemo(() => {
    switch (state) {
      case 'cart':
        return formatCartMessage({ id: 'myCart', defaultMessage: 'Cart' });
      case 'wishlist':
        return formatWishlistMessage({ id: 'my.wishlist', defaultMessage: 'Wishlist' });
    }
  }, [formatCartMessage, formatWishlistMessage, state]);

  const ActiveSection = useMemo(
    () =>
      ({
        cart: (
          <Cart
            cart={cart}
            isEmpty={isEmpty}
            onApplyDiscountCode={onApplyDiscountCode}
            onRemoveDiscountCode={onRemoveDiscountCode}
            emptyStateImage={emptyCartImage}
            emptyStateTitle={emptyCartTitle}
            emptyStateSubtitle={emptyCartSubtitle}
            emptyStateCategories={emptyCartCategories}
            handleCategoryClick={onClose}
            onRemoveItem={onRemoveItem}
            onUpdateItem={onUpdateItem}
            OnMoveToWishlist={OnMoveToWishlist}
          />
        ),
        wishlist: (
          <Wishlist
            wishlist={wishlist}
            onRemoveFromWishlist={onRemoveFromWishlist}
            onMoveToCart={onMoveToCart}
            onClearWishlist={onClearWishlist}
            emptyWishlistTitle={emptyWishlistTitle}
            emptyWishlistSubtitle={emptyWishlistSubtitle}
            emptyWishlistCategories={emptyWishlistCategories}
            emptyWishlistImage={emptyWishlistImage}
          />
        ),
      })[state as MenuState] ?? <></>,
    [
      cart,
      isEmpty,
      onApplyDiscountCode,
      onRemoveDiscountCode,
      onRemoveItem,
      onUpdateItem,
      OnMoveToWishlist,
      wishlist,
      onRemoveFromWishlist,
      onMoveToCart,
      onClearWishlist,
      emptyCartCategories,
      emptyCartImage,
      emptyCartSubtitle,
      emptyCartTitle,
      emptyWishlistCategories,
      emptyWishlistImage,
      emptyWishlistSubtitle,
      emptyWishlistTitle,
      onClose,
      state,
    ],
  );

  return (
    <div
      className={`flex h-screen w-screen flex-col justify-between md:h-auto md:max-h-[calc(100vh-200px)] md:w-[500px] lg:max-h-[calc(100vh-190px)]`}
    >
      <div className="flex shrink-0 items-center justify-between border-b border-neutral-400 px-16 py-12">
        <h3 className="text-base font-medium leading-normal">
          {title}, <span className="text-gray-600">{state === 'cart' ? totalCartItems : totalWishlistItems} items</span>
        </h3>
        <div onClick={onClose} className="flex h-full cursor-pointer items-center transition hover:opacity-80">
          <span className="text-sm text-gray-600">Close</span>
          <CloseIcon className="-mr-7 size-28" stroke="#494949" />
        </div>
      </div>
      {ActiveSection}
    </div>
  );
};

export default Slideout;
